"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, AnimatePresence } from "motion/react";
import { ChevronDown, X } from "lucide-react";
import { cn } from "../lib/cn";
import { springs, transitions } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
} from "../primitives/DropdownMenu";

/* ============================================================================
 * Types
 * ========================================================================= */

/** A single selectable filter value. */
export interface FilterOption {
  /** Stable value committed to `onValueChange`. */
  value: string;
  /** Visible label in the menu (and, when active, in the chip). */
  label: string;
}

/** Visual tone of an active chip. `sage` is the brand-positive treatment. */
export type FilterChipTone = "sage" | "neutral";

export interface FilterChipProps {
  /** Leading label, e.g. `"Role"`. Rendered as `Label: Value` in the chip. */
  label: string;
  /**
   * Selectable options. In single mode the chip shows the matched option's
   * label; in `multi` mode it shows the count of selected values.
   */
  options: FilterOption[];

  /**
   * Single-select value (controlled). Active state derives from
   * `value !== defaultValue`. Required unless `multi` is set.
   */
  value?: string;
  /** Single-select default. Clearing resets `value` to this. Default `""`. */
  defaultValue?: string;
  /** Single-select change handler. Fired with the chosen option value. */
  onValueChange?: (next: string) => void;

  /** Switch to multi-select (checkbox) mode. */
  multi?: boolean;
  /** Multi-select values (controlled). Active when non-empty. */
  values?: string[];
  /** Multi-select change handler. Fired with the next full selection. */
  onValuesChange?: (next: string[]) => void;

  /** Active-state color. Default `"sage"`. */
  tone?: FilterChipTone;
  /** Disable the chip. */
  disabled?: boolean;
  /**
   * Accessible name for the trigger. Defaults to `"<label> filter"`. The
   * trigger also carries Radix's `aria-expanded` automatically.
   */
  "aria-label"?: string;
  /** Extra classes for the chip trigger. */
  className?: string;
  /** Alignment of the menu relative to the trigger. Default `"start"`. */
  align?: "start" | "center" | "end";
}

/* ============================================================================
 * Chip tone tokens
 * ========================================================================= */

// Idle + active tints, mapped to the library's shadcn-aliased tokens. The
// donor (`unified/Studios.tsx`) used a raw `rgba(58,143,92,...)` sage wash for
// the active state and `surface / hairline-strong / ink-3` for idle. Those
// re-map cleanly onto the lib's `success`-soft idiom (see Badge `success`:
// `border-success/30 bg-success/15 text-success`) and the neutral
// `border-input bg-background text-muted-foreground` field look.
const CHIP_IDLE =
  "border-input bg-background text-muted-foreground hover:border-ring hover:text-foreground";

const CHIP_ACTIVE: Record<FilterChipTone, string> = {
  sage: "border-success/40 bg-success/15 text-success hover:border-success/50",
  neutral: "border-ring bg-muted text-foreground hover:border-ring",
};

// The clear-X hit area tint, keyed to the same tone so it reads as part of the
// active chip rather than a foreign control.
const CLEAR_HOVER: Record<FilterChipTone, string> = {
  sage: "hover:bg-success/20",
  neutral: "hover:bg-foreground/10",
};

/* ============================================================================
 * FilterChip
 * ========================================================================= */

/**
 * FilterChip — a pill-shaped dropdown filter built on the library's Radix
 * `DropdownMenu`. Idle it shows `Label` + a chevron; active (when the value
 * differs from `defaultValue`, or any value is selected in `multi` mode) it
 * fills with the tone tint, shows `Label: Value`, and swaps the chevron for an
 * inline clear-X that resets to the default without reopening the menu.
 *
 * Radix gives focus trap, type-ahead, arrow-key nav, Escape-to-close, and
 * click-away for free — this replaces the hand-rolled `useClickAway` + manual
 * `menu` state machine in the donor surfaces. The selected check mark comes
 * from `DropdownMenuRadioItem` / `DropdownMenuCheckboxItem`'s built-in
 * indicator.
 *
 * Motion: the idle <-> active morph (border / background / text) eases via the
 * `transitions.hover` preset; the clear-X enters with a `springs.snappy`
 * scale. Both collapse to a static final state under reduced motion. The menu
 * entrance is inherited from `DropdownMenuContent` (already reduced-motion
 * aware).
 *
 * @example Single-select (the Studios contract)
 * ```tsx
 * <FilterChip
 *   label="Role"
 *   value={role}
 *   defaultValue="all"
 *   onValueChange={setRole}
 *   options={[
 *     { value: "all", label: "All roles" },
 *     { value: "owner", label: "Owner" },
 *     { value: "editor", label: "Editor" },
 *   ]}
 * />
 * ```
 *
 * @example Multi-select (future tag filters)
 * ```tsx
 * <FilterChip
 *   label="Tags"
 *   multi
 *   values={tags}
 *   onValuesChange={setTags}
 *   options={tagOptions}
 * />
 * ```
 */
export const FilterChip = React.forwardRef<HTMLButtonElement, FilterChipProps>(
  (
    {
      label,
      options,
      value,
      defaultValue = "",
      onValueChange,
      multi = false,
      values,
      onValuesChange,
      tone = "sage",
      disabled = false,
      className,
      align = "start",
      "aria-label": ariaLabel,
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const animate = reduced === false;

    const selectedValues = values ?? [];
    const active = multi ? selectedValues.length > 0 : value !== defaultValue;

    // Display text: single-select shows the matched option label; multi shows
    // a count so the chip width stays bounded regardless of how many are set.
    const selectedOption = multi ? undefined : options.find((o) => o.value === value);
    const valueText = multi
      ? `${selectedValues.length} selected`
      : (selectedOption?.label ?? "");

    const handleClear = (e: React.MouseEvent) => {
      // stopPropagation so the click clears the filter rather than toggling the
      // Radix trigger open (the donor's `clearPill` contract).
      e.stopPropagation();
      if (multi) onValuesChange?.([]);
      else onValueChange?.(defaultValue);
    };

    const toggleMulti = (optValue: string, checked: boolean) => {
      const next = checked
        ? [...selectedValues, optValue]
        : selectedValues.filter((v) => v !== optValue);
      onValuesChange?.(next);
    };

    const morphTransition = animate ? transitions.hover : { duration: 0 };

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <motion.button
            ref={ref}
            type="button"
            disabled={disabled}
            aria-label={ariaLabel ?? `${label} filter`}
            // Animate the tint morph rather than snapping. `animate` here is
            // motion's prop (target values), distinct from the `animate` flag.
            animate={{}}
            transition={morphTransition}
            className={cn(
              "inline-flex h-8 items-center gap-1.5 rounded-full border px-3",
              "text-xs font-medium outline-none",
              "transition-colors duration-180 ease-smooth-out",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
              "disabled:pointer-events-none disabled:opacity-50",
              active ? CHIP_ACTIVE[tone] : CHIP_IDLE,
              className,
            )}
          >
            <span className={active ? "opacity-90" : ""}>{label}</span>
            {active && valueText ? (
              <>
                <span aria-hidden className="opacity-60">
                  :
                </span>
                <span className="font-semibold">{valueText}</span>
              </>
            ) : null}
            <AnimatePresence initial={false} mode="wait">
              {active ? (
                <motion.span
                  key="clear"
                  role="button"
                  tabIndex={-1}
                  aria-label={`Clear ${label} filter`}
                  onClick={handleClear}
                  // Pointer-down must not bubble to the Radix trigger or the
                  // menu opens behind the cleared chip.
                  onPointerDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClear(e as unknown as React.MouseEvent);
                    }
                  }}
                  initial={animate ? { scale: 0, opacity: 0 } : false}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={animate ? { scale: 0, opacity: 0 } : { opacity: 0 }}
                  transition={animate ? springs.snappy : { duration: 0 }}
                  className={cn(
                    "-mr-1 inline-flex size-[15px] items-center justify-center rounded-full",
                    "cursor-pointer transition-colors",
                    CLEAR_HOVER[tone],
                  )}
                >
                  <X className="size-3" aria-hidden />
                </motion.span>
              ) : (
                <motion.span
                  key="chevron"
                  initial={animate ? { opacity: 0 } : false}
                  animate={{ opacity: 1 }}
                  exit={animate ? { opacity: 0 } : { opacity: 0 }}
                  transition={animate ? { duration: 0.12 } : { duration: 0 }}
                  className="-mr-0.5 inline-flex"
                >
                  <ChevronDown className="size-3 opacity-70" aria-hidden />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align={align} className="min-w-[200px]">
          {multi ? (
            options.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selectedValues.includes(opt.value)}
                onCheckedChange={(checked) => toggleMulti(opt.value, checked === true)}
                // Keep the menu open across multi-select toggles.
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))
          ) : (
            <DropdownMenuRadioGroup
              value={value}
              onValueChange={(next) => onValueChange?.(next)}
            >
              {options.map((opt) => (
                <DropdownMenuRadioItem key={opt.value} value={opt.value}>
                  {opt.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);
FilterChip.displayName = "FilterChip";

/* ============================================================================
 * FilterBar
 * ========================================================================= */

const filterBarVariants = cva("flex w-full flex-col", {
  variants: {
    size: {
      /** Studios default — comfortable spacing, search above the chip row. */
      md: "gap-3",
      /** Agents compact — tighter gap for dense toolbars. */
      sm: "gap-2",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const chipRowVariants = cva("flex items-center", {
  variants: {
    size: {
      md: "gap-2.5",
      sm: "gap-2",
    },
    wrap: {
      true: "flex-wrap gap-y-2",
      false: "",
    },
  },
  defaultVariants: {
    size: "md",
    wrap: true,
  },
});

export interface FilterBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof filterBarVariants> {
  /**
   * Search control. Drop in a configured `<SmartSearch>` (or any node), or
   * omit for a chip-only bar. Rendered on its own row above the chips.
   */
  searchSlot?: React.ReactNode;
  /**
   * The filter chips. Pass `<FilterChip>` children; the trailing `sort` chip
   * (if any) goes in the `sort` prop so it can be right-aligned independently.
   */
  children?: React.ReactNode;
  /**
   * The sort chip, kept distinct so `align="spread"` can push it to the far
   * right of the chip row (reproducing the donor's `flex-1` spacer).
   */
  sort?: React.ReactNode;
  /**
   * Consumer-owned header actions (view toggle, Create button, permission
   * gating). Out of scope for the bar's filtering concern — rendered at the
   * far right of the chip row, after `sort`.
   */
  right?: React.ReactNode;
  /**
   * Chip-row alignment. `"spread"` inserts a flex spacer so `sort` + `right`
   * sit at the far right; `"start"` keeps everything left-packed. Default
   * `"spread"`.
   */
  align?: "start" | "spread";
  /** Wrap chips onto multiple lines when they overflow. Default `true`. */
  wrap?: boolean;
}

/**
 * FilterBar — a config-driven listing toolbar: an optional `SmartSearch` slot,
 * a row of `FilterChip` controls, a right-aligned sort chip, and a slot for
 * consumer-owned header actions. Consolidates the hand-built search + filter +
 * sort toolbars duplicated across the listing surfaces into one accessible,
 * on-brand, animated primitive.
 *
 * It owns layout + composition only: search, filter chips, and sort. View
 * toggles, Create buttons, and permission gating stay with the consumer and
 * are passed via `right`.
 *
 * Sizes: `md` (Studios default) and `sm` (Agents compact). Chips animate their
 * active morph + clear-X and inherit the Radix menu entrance; everything
 * degrades to a static state under reduced motion.
 *
 * @example Studios — search + 4 chips (3 filters + sort), spread layout
 * ```tsx
 * <FilterBar
 *   searchSlot={
 *     <SmartSearch
 *       value={search}
 *       onChange={setSearch}
 *       placeholderPrompts={["Search your studios...", "Find a studio by name..."]}
 *     />
 *   }
 *   sort={
 *     <FilterChip label="Sort" value={sort} defaultValue="recent" onValueChange={setSort} options={sortOptions} />
 *   }
 * >
 *   <FilterChip label="Role" value={role} defaultValue="all" onValueChange={setRole} options={roleOptions} />
 *   <FilterChip label="Visibility" value={vis} defaultValue="all" onValueChange={setVis} options={visOptions} />
 *   <FilterChip label="Status" value={status} defaultValue="active" onValueChange={setStatus} options={statusOptions} />
 * </FilterBar>
 * ```
 *
 * @example Agents — compact preset
 * ```tsx
 * <FilterBar size="sm" searchSlot={<SmartSearch value={q} onChange={setQ} />}>
 *   <FilterChip label="Saved view" value={view} defaultValue="all" onValueChange={setView} options={viewOptions} />
 *   <FilterChip label="Filters" multi values={filters} onValuesChange={setFilters} options={filterOptions} />
 * </FilterBar>
 * ```
 */
export const FilterBar = React.forwardRef<HTMLDivElement, FilterBarProps>(
  (
    { className, size = "md", searchSlot, children, sort, right, align = "spread", wrap = true, ...rest },
    ref,
  ) => {
    const hasChips = React.Children.count(children) > 0;
    const hasTail = Boolean(sort) || Boolean(right);

    return (
      <div ref={ref} className={cn(filterBarVariants({ size }), className)} {...rest}>
        {searchSlot ? <div className="w-full">{searchSlot}</div> : null}

        {hasChips || hasTail ? (
          <div className={cn(chipRowVariants({ size, wrap }))}>
            {children}
            {align === "spread" && hasTail ? (
              <div className="flex-1" aria-hidden />
            ) : null}
            {sort}
            {right ? <div className="flex items-center gap-2">{right}</div> : null}
          </div>
        ) : null}
      </div>
    );
  },
);
FilterBar.displayName = "FilterBar";
